import { useSelector } from '@xstate/react'
import * as React from 'react'
import { InterpreterFrom, StateFrom } from 'xstate'

import { getOrderFromUrl } from '../../helpers/getOrderFromUrl'
import { authMachine } from '../service/authMachine'
import { GUEST_ID } from '../service/authMachineTypes'

type TAuthState = StateFrom<typeof authMachine>

const userTokenSelector = (state: TAuthState) => state.context.userToken
const guestTokenSelector = (state: TAuthState) => state.context.guestToken
const userIdSelector = (state: TAuthState) => state.context.userId
const errorSelector = (state: TAuthState) => state.context.error

type TOptions = {
  machine: InterpreterFrom<typeof authMachine>
  isLoginEnabled: boolean
}

/**
 * Returns:
 *  - the `userId` & `authToken` that should be used throughout the app
 *
 * If the token is provided as a URL param, returns that one,
 * otherwise returns whatever is stored in the auth service
 * that is responsible for fetching it/loading it from persistence
 *
 * also returns:
 *  - `userToken` of the currently logged in user/guest (that is possibly different from the _authToken_ if the _authToken_ was parsed from url)
 *  - `error` if authentication has failed
 *
 *  - `guestToken` that should be always defined (useful for transferring the order)
 *  - `loggedInUserToken` of the logged in user (useful for transferring the order)
 */
export const useAuthContextValues = ({ machine, isLoginEnabled }: TOptions) => {
  const { userToken: urlAuthToken } = getOrderFromUrl()

  const userToken = useSelector(machine, userTokenSelector)
  const guestToken = useSelector(machine, guestTokenSelector)
  const userId = useSelector(machine, userIdSelector)
  const error = useSelector(machine, errorSelector)

  const contextValues = React.useMemo(() => {
    // if login is disabled, we behave as if the user is a guest
    if (!isLoginEnabled) {
      return {
        authToken: urlAuthToken ?? guestToken,
        userToken: guestToken,
        guestToken,
        loggedInUserToken: null,
        userId: GUEST_ID,
        error,
      }
    }

    // guest context
    if (userId === GUEST_ID) {
      return {
        authToken: urlAuthToken ?? guestToken,
        userToken: guestToken,
        guestToken,
        loggedInUserToken: userToken,
        userId,
        error,
      }
    }

    // logged in user context
    return {
      authToken: urlAuthToken ?? userToken,
      userToken,
      guestToken,
      loggedInUserToken: userToken,
      userId,
      error,
    }
  }, [userToken, error, guestToken, isLoginEnabled, urlAuthToken, userId])

  return contextValues
}
